/**
 * f12.3: the lock's reads and its one write (design D8).
 *
 * The preview is read only while the dialog is open. A successful freeze
 * invalidates the cycle (its detail, forecast grid, preview and snapshot keys
 * all sit under `['cycle', id]`), the review and the cycle list. A `409` keeps
 * the dialog open with the server's message and refetches the preview, so the
 * blockers and the version shown are the server's current ones.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export function useFreezePreview(cycleId: string, open: boolean) {
  return useQuery({
    queryKey: queryKeys.cycle.freezePreview(cycleId),
    queryFn: () => api.getCycleFreezePreview(cycleId),
    enabled: open && Boolean(cycleId),
    retry: false,
    staleTime: 0,
  });
}

export function useFreezeCycle(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expectedVersion: number) =>
      api.freezeCycle(cycleId, { expected_version: expectedVersion }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId) });
      void queryClient.invalidateQueries({ queryKey: ['review', cycleId] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycles.all() });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.freezePreview(cycleId) });
        // A version conflict means the cycle moved: the page head must show it too.
        void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId), exact: true });
      }
    },
  });
}

/** The snapshot history, newest first. Finance and Admin; others get a 403. */
export function useSnapshots(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.snapshots(cycleId),
    queryFn: () => api.listCycleSnapshots(cycleId),
    enabled: Boolean(cycleId),
    retry: false,
  });
}

/** The dialog's inline refusal: the server's own message, whatever the reason. */
export function freezeFailure(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : 'The cycle could not be locked.';
}
